import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";

/** Get token from the saved user object */
const getToken = () => {
  try {
    return JSON.parse(localStorage.getItem("user"))?.token || null;
  } catch {
    return null;
  }
};

/** Normalize emails for safe comparisons */
const normalizeEmail = (e) => (e || "").trim().toLowerCase();
const sameEmail = (a, b) => normalizeEmail(a) === normalizeEmail(b);

/** Support both CRA and Vite env styles */
const ENV_SUPERADMIN_EMAIL =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_SUPERADMIN_EMAIL) ||
  process.env.REACT_APP_SUPERADMIN_EMAIL ||
  "";

/** API base for both CRA and Vite projects */
const ENV_API_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE) ||
  process.env.REACT_APP_API_BASE_URL ||
  "";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [expandedPlans, setExpandedPlans] = useState({});

  const [currentUserEmail, setCurrentUserEmail] = useState("");
  const [currentUserId, setCurrentUserId] = useState("");
  const [processingIds, setProcessingIds] = useState(new Set());

  const SUPREME_EMAIL = normalizeEmail(ENV_SUPERADMIN_EMAIL);
  const API_BASE = ENV_API_BASE;

  // ✅ FIX 1: Compute isSuperadmin dynamically based on currentUserEmail
  const isSuperadmin =
    currentUserEmail && SUPREME_EMAIL
      ? sameEmail(currentUserEmail, SUPREME_EMAIL)
      : false;

  /** 1) Fetch current user */
  const fetchCurrentUser = async () => {
    const token = getToken();
    if (!token) return;
    try {
      const res = await axios.get(`${API_BASE}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const email = res?.data?.email || "";
      const id = res?.data?._id || "";
      setCurrentUserEmail(normalizeEmail(email));
      setCurrentUserId(id);
    } catch (err) {
      console.error("Error fetching current user:", err);
    }
  };

  /** 2) Fetch users */
  const fetchUsers = async () => {
    const token = getToken();
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const list = Array.isArray(res.data) ? res.data : [];

      const filtered = list.filter((u) => !sameEmail(u?.email, SUPREME_EMAIL));
      const finalList = isSuperadmin
        ? filtered.filter((u) => u?._id !== currentUserId)
        : filtered;

      setUsers(finalList);
    } catch (err) {
      console.error("Error fetching users:", err);
      alert("Failed to fetch users.");
    } finally {
      setLoading(false);
    }
  };

  /** 3) Fetch pending requests */
  const fetchPendingRequests = async () => {
    const token = getToken();
    if (!token) return;
    try {
      const url = isSuperadmin
        ? `${API_BASE}/api/admin/queue-action`
        : `${API_BASE}/api/admin/queue-action/my-pending`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPendingRequests(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching pending requests:", err);
      setPendingRequests([]);
    }
  };

  useEffect(() => {
    (async () => {
      await fetchCurrentUser();
    })();
  }, []);

  // ✅ FIX 2: Once we know currentUserEmail, fetch users & requests
  useEffect(() => {
    if (currentUserEmail) {
      fetchUsers();
      fetchPendingRequests();
    }
  }, [currentUserEmail, isSuperadmin]);

  /** Helper */
  const hasPendingRequest = (userId, actionType) =>
    pendingRequests.some(
      (r) => r?.targetUser?._id === userId && r?.action === actionType
    );

  /** Direct endpoints */
  const directPromote = async (userId) => {
    const token = getToken();
    return axios.put(
      `${API_BASE}/api/superadmin/users/${userId}/promote`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
  };
  const directDemote = async (userId) => {
    const token = getToken();
    return axios.put(
      `${API_BASE}/api/superadmin/users/${userId}/demote`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
  };
  const directDelete = async (userId) => {
    const token = getToken();
    return axios.delete(`${API_BASE}/api/superadmin/users/${userId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  };

  /** Queue for non-superadmin */
  const queueAction = async (userId, actionType) => {
    const token = getToken();
    return axios.post(
      `${API_BASE}/api/admin/queue-action`,
      { action: actionType, targetUserId: userId },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  };

  /** Delete handler */
  const handleDelete = async (id, email) => {
    if (sameEmail(email, currentUserEmail)) {
      alert("❌ You cannot delete yourself.");
      return;
    }
    if (hasPendingRequest(id, "delete")) return;
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    if (processingIds.has(id)) return;

    setProcessingIds((prev) => new Set(prev).add(id));
    try {
      if (isSuperadmin) {
        await directDelete(id);
        alert("✅ User deleted immediately.");
      } else {
        await queueAction(id, "delete");
        alert("✅ Deletion request queued.");
      }
      await fetchUsers();
    } catch (err) {
      console.error("Delete error:", err);
      alert("❌ Failed to delete / queue deletion.");
    } finally {
      setProcessingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  /** Role change */
  const handleRoleChange = async (id, currentRole, email) => {
    if (sameEmail(email, currentUserEmail)) {
      alert("❌ You cannot change your own role.");
      return;
    }
    if (processingIds.has(id)) return;

    const actionType = currentRole === "admin" ? "demote" : "promote";
    if (hasPendingRequest(id, actionType)) return;

    setProcessingIds((prev) => new Set(prev).add(id));
    try {
      if (isSuperadmin) {
        if (actionType === "promote") await directPromote(id);
        else await directDemote(id);
        alert(
          `✅ ${actionType === "promote" ? "Promoted to admin." : "Demoted to user."}`
        );
      } else {
        await queueAction(id, actionType);
        alert(
          `✅ ${actionType === "promote" ? "Promotion" : "Demotion"} request queued.`
        );
      }
      await fetchUsers();
    } catch (err) {
      console.error("Role change error:", err);
      alert("❌ Failed to change role / queue request.");
    } finally {
      setProcessingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const filteredUsers = users.filter((u) => {
    const q = search.toLowerCase();
    const matchesText =
      (u.username || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q);

    if (filter === "paid") return matchesText && u.hasPaid;
    if (filter === "unpaid") return matchesText && !u.hasPaid;
    if (filter === "pending") {
      const hasAnyPending = ["promote", "demote", "delete"].some((a) =>
        hasPendingRequest(u._id, a)
      );
      return matchesText && hasAnyPending;
    }
    return matchesText;
  });

  const groupedUsers = {
    Admins: filteredUsers.filter((u) => u.role === "admin"),
    Users: filteredUsers.filter((u) => u.role === "user"),
  };

  // ✅ FIX 3: Debug log to verify logic
  console.log("🧠 Superadmin Debug:", {
    currentUserEmail,
    SUPREME_EMAIL,
    isSuperadmin,
  });

  const togglePlans = (userId) => {
  setExpandedPlans((prev) => ({
    ...prev,
    [userId]: !prev[userId],
  }));
};

  return (
    <div className="p-6">
      <h2 className="text-2xl font-semibold mb-4 text-gray-800">User Management</h2>

      {/* Search + Filter */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <input
          type="text"
          placeholder="Search by username or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="p-2 border rounded-lg w-full md:w-1/2 focus:ring focus:ring-blue-200"
        />
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="p-2 border rounded-lg w-full md:w-1/4 focus:ring focus:ring-blue-200"
        >
          <option value="all">All users</option>
          <option value="paid">Paid</option>
          <option value="unpaid">Unpaid</option>
          <option value="pending">Pending Requests</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-600">
          <Loader2 className="animate-spin" size={20} /> Loading users...
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="text-gray-500 bg-gray-50 border border-gray-200 p-6 rounded-lg text-center">
          No users found.
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {Object.entries(groupedUsers).map(([groupName, groupUsers]) => (
            <div key={groupName}>
              <h3 className="font-bold text-lg mb-3 text-gray-700">
                {groupName} ({groupUsers.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {groupUsers.map((user) => {
                  const promotePending = hasPendingRequest(user._id, "promote");
                  const demotePending = hasPendingRequest(user._id, "demote");
                  const deletePending = hasPendingRequest(user._id, "delete");
                  const isSelfRow = sameEmail(user.email, currentUserEmail);

                  return (
                    <div
                      key={user._id}
                      className={`p-4 bg-white rounded-lg border shadow-sm hover:shadow-md transition ${
                        promotePending || demotePending || deletePending
                          ? "animate-pulse"
                          : ""
                      }`}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
  <h4 className="font-semibold text-lg text-gray-900">
    {user.username || "Unnamed"}
  </h4>  
</div>
                        <div className="flex items-center gap-2">                          
                          <div className="flex flex-wrap gap-2">
  <span
    className={`px-3 py-1 rounded-full text-xs font-semibold ${
      user.hasPaid
        ? "bg-emerald-100 text-emerald-700"
        : "bg-gray-100 text-gray-600"
    }`}
  >
    {user.hasPaid ? "Premium" : "Free"}
  </span>

  <span
    className={`px-3 py-1 rounded-full text-xs font-semibold ${
      user.role === "admin"
        ? "bg-blue-100 text-blue-700"
        : "bg-purple-100 text-purple-700"
    }`}
  >
    {user.role}
  </span>
</div>                         
                        </div>
                      </div>

                      <p className="text-sm text-gray-600">{user.email}</p>

{/* ================= ACCOUNT INFORMATION ================= */}

<div className="mt-4 border-t pt-3">
  <h5 className="font-semibold text-gray-800 mb-2">
    Account Information
  </h5>

  <p className="text-sm">
    Email Verification:
    <span
      className={`ml-2 font-medium ${
        user.emailVerified
          ? "text-green-600"
          : "text-red-600"
      }`}
    >
      {user.emailVerified
        ? "✅ Verified"
        : "❌ Not Verified"}
    </span>
  </p>

  <p className="text-sm mt-1">
    Joined:
    <span className="ml-2">
      {user.createdAt
        ? new Date(user.createdAt).toLocaleDateString("en-IN")
        : "N/A"}
    </span>
  </p>

  <p className="text-sm mt-1">
    Last Login:
    <span className="ml-2">
      {user.lastLogin
        ? new Date(user.lastLogin).toLocaleString("en-IN")
        : "Never"}
    </span>
  </p>

  <p className="text-sm mt-1">
    Last Active:
    <span className="ml-2">
      {user.lastActiveAt
        ? new Date(user.lastActiveAt).toLocaleString("en-IN")
        : "Never"}
    </span>
  </p>
</div>

{/* ================= SUBSCRIPTION ================= */}

<div className="mt-4 border-t pt-3">
  <h5 className="font-semibold text-gray-800 mb-2">
    Subscription Information
  </h5>

  <p className="text-sm">
    Main Course:
    <span
      className={`ml-2 font-medium ${
        user.hasPaid
          ? "text-green-600"
          : "text-gray-500"
      }`}
    >
      {user.hasPaid
        ? "Premium"
        : "Free"}
    </span>
  </p>

  {user.firstPaidAt && (
    <p className="text-sm mt-1">
      Purchased:
      <span className="ml-2">
        {new Date(user.firstPaidAt).toLocaleDateString("en-IN")}
      </span>
    </p>
  )}

  {user.subscriptionExpiresAt && (
    <p className="text-sm mt-1">
      Expires:
      <span className="ml-2">
        {new Date(
          user.subscriptionExpiresAt
        ).toLocaleDateString("en-IN")}
      </span>
    </p>
  )}
</div>

<div className="text-sm mt-1">
  <p>
    Study Plans:
    <span className="ml-1 font-medium text-blue-600">
      {user.purchasedPlans?.length || 0}
    </span>
  </p>

  {user.purchasedPlans?.length > 0 && (
  <div className="mt-2">
    <button
      onClick={() => togglePlans(user._id)}
      className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-2"
    >
      {expandedPlans[user._id]
        ? "▼ Hide Study Plans"
        : `▶ View Study Plans (${user.purchasedPlans.length})`}
    </button>

    {expandedPlans[user._id] && (
      <div className="mt-3 space-y-2">
        {user.purchasedPlans.map((plan, index) => (
          <div
            key={plan._id || index}
            className="bg-gray-50 border rounded-xl p-3"
          >
            <p className="font-medium text-blue-700">
              {plan.planId?.title || "Study Plan"}
            </p>

            <p className="text-xs text-gray-600">
              Purchased:
              {" "}
              {plan.purchasedAt
                ? new Date(plan.purchasedAt).toLocaleDateString("en-IN")
                : "N/A"}
            </p>

            <p className="text-xs text-gray-600">
              Expires:
              {" "}
              {plan.expiresAt
                ? new Date(plan.expiresAt).toLocaleDateString("en-IN")
                : "N/A"}
            </p>

            {plan.amountPaid > 0 && (
              <p className="text-green-700 font-medium mt-1">
                ₹{plan.amountPaid}
              </p>
            )}
          </div>
        ))}
      </div>
    )}
  </div>
)}
</div>

                      <div className="mt-2 flex flex-wrap gap-2">
                        {isSelfRow ? (
                          <span className="text-gray-500 italic">
                            Actions disabled for yourself
                          </span>
                        ) : (
                          <>
                            <button
                              onClick={() =>
                                handleRoleChange(user._id, user.role, user.email)
                              }
                              disabled={
                                processingIds.has(user._id) ||
                                (user.role === "admin"
                                  ? demotePending
                                  : promotePending)
                              }
                              className={`flex items-center gap-1 px-3 py-1 rounded-lg ${
                                isSuperadmin
                                  ? "bg-gradient-to-r from-blue-500 to-indigo-500 hover:bg-blue-600 text-white"
                                  : "bg-yellow-500 hover:bg-yellow-600 text-white"
                              } disabled:opacity-50 text-sm`}
                            >
                              {isSuperadmin ? (
                                user.role === "admin" ? (
                                  "Demote to User"
                                ) : (
                                  "Promote to Admin"
                                )
                              ) : user.role === "admin" ? (
                                demotePending
                                  ? "⏳ Pending Demotion"
                                  : "Request Demotion"
                              ) : promotePending ? (
                                "⏳ Pending Promotion"
                              ) : (
                                "Request Promotion"
                              )}
                            </button>

                            <button
                              onClick={() => handleDelete(user._id, user.email)}
                              disabled={processingIds.has(user._id) || deletePending}
                              className={`flex items-center gap-1 px-3 py-1 rounded-lg ${
                                isSuperadmin
                                  ? "bg-gradient-to-r from-red-500 to-rose-500 hover:bg-red-600 text-white"
                                  : "bg-orange-500 hover:bg-orange-600 text-white"
                              } disabled:opacity-50 text-sm`}
                            >
                              {isSuperadmin
                                ? "Delete User"
                                : deletePending
                                ? "⏳ Pending Deletion"
                                : "Request Deletion"}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserManagement;
